import { asyncTimeout } from "@use/async-timeout";
import { Deferred } from "@use/deferred";

/**
 * Used on any async method to create a debounced version, that'll be triggered
 * only once after the given delay.
 */
export const debounce = (delay: number) => {
  return (target: {}, propertyKey: string, descriptor: PropertyDescriptor) => {
    // Create unique symbols for this instance of the decorated method
    const autoIncrementedIdSymbol = Symbol(
      "@use/decorators.debounced.autoIncrementedId"
    );
    const queueSymbol = Symbol("@use/decorators.debounced.queue");

    return {
      // Forward configurable and enumerable "as is"
      configurable: descriptor.configurable,
      enumerable: descriptor.enumerable,

      // Overwrite the callback, so that we can make sure it'll only be
      // triggered once every time the delay occurs
      async value(...args: unknown[]): Promise<any> {
        // tslint:disable-line no-any
        // Create a strongly typed version of the current execution
        // context, so that we can apply / extract queue and id from it
        // @ts-expect-error
        const context = this as unknown & {
          [autoIncrementedIdSymbol]: number;
          [queueSymbol]: Deferred<unknown>[];
        };

        // Generate a new, unique id for this execution context of our
        // debounced method
        const id = (context[autoIncrementedIdSymbol] = context[
          autoIncrementedIdSymbol
        ]
          ? context[autoIncrementedIdSymbol] + 1
          : 1);

        // Extract callback queue currently applied for this method
        const queue = (context[queueSymbol] = context[queueSymbol] || []);

        // Wait the defined delay
        await asyncTimeout(delay);

        // If another request has been triggered while we were waiting
        // for the delay to time out, then we cannot begin execution of
        // the callback yet...
        // Instead we'll need to create and return a deferred object
        // that's pushed to the execution queue, so we can resolve it
        // once the request actually finishes
        if (id !== context[autoIncrementedIdSymbol]) {
          // Create the deferred object
          const deferred = new Deferred<unknown>();

          // Push the object to our execution queue, so that we can
          // resolve it later on
          queue.push(deferred);

          // Return the object for now, there's nothing more to do
          // right now!
          return deferred;
        }

        // Clone the execution queue, so that we can properly resolve
        // the deferred return values available right now, while
        // beginning to generate a new queue for future executions
        const currentQueue = queue.splice(0);

        try {
          // If we get here, it's time to execute the original
          // callback!
          // tslint:disable-next-line no-invalid-this
          const result = await (<Promise<unknown>>(
            (<(...args: unknown[]) => unknown>descriptor.value).call(
              this,
              ...args
            )
          ));

          // Once the result from the original callback has been
          // returned, we're going to propagate it's value to all
          // queued deferred return values
          for (const callback of currentQueue) {
            callback.resolve(result);
          }

          // And finally return the result to the current execution
          // context
          return result;
        } catch (err) {
          // If an error occurred during execution of the original
          // callback, then propagate it to all queued deferred
          // return values
          for (const deferred of currentQueue) {
            deferred.reject(<unknown>err);
          }

          // ... And propagate the error to the current execution
          // context as well!
          throw err;
        }
      },
    };
  };
};
